import { Button, NoticeBox, StatCard } from "../ui";
import { RequestProgress } from "./ChecksStep";
import { IssueList } from "./IssueDetails";
import { appLabel, sectionLabel } from "./reportAnalysisModel";
import { RETRYABLE } from "./reviewModel";
import { staleCount } from "./PreviewStep";
import { count, periodLabel } from "./format";
import { errorText } from "./reportAnalysisText";

function Pairs({ items }) {
  return (
    <dl className="report-review-pairs">
      {items.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
    </dl>
  );
}

// Step 5 - Save & Result. While saving: the running request (real upload progress only). After a
// save: what the server saved (the temporary files are already cleared). After a failure: where and
// why, and whether anything was saved - only "nothing was saved" when the server says so (saved: false).
export default function ResultStep({ text, lang, state, review, savingApps, onRetry, onBackToPreview, onBackToFiles, onStartNew }) {
  const f = text.flow;
  const { result, error } = state;
  const saving = state.phase === "committing" || (state.phase !== "idle" && !result && !error);
  const context = { storeName: review.context.storeName, period: review.context.period };

  if (saving) {
    return (
      <section className="report-analysis-step" data-testid="step-result" data-state="saving" aria-label={f.savingTitle} aria-busy="true">
        <header className="report-analysis-step__head"><h3>{f.savingTitle}</h3><p className="report-analysis-muted">{f.savingRunning}</p></header>
        <RequestProgress text={text} phase={state.phase} progress={state.progress} label={typeof state.progress === "number" && state.progress < 1 ? f.uploadProgress : f.serverWorking} />
        {savingApps.length > 0 && <p><strong>{f.savingApps}:</strong> {savingApps.map((app) => appLabel(app, lang)).join("، ")}</p>}
        <div className="report-analysis-actions"><Button variant="primary" appearance="filled" loading disabled>{f.confirmSave}</Button></div>
      </section>
    );
  }

  if (result) {
    const rows = result.rows || {};
    // Source rows analyzed vs excluded (backend accounting): excluded rows were not saved.
    const accounting = result.analysisSummary?.totals?.accounting || null;
    const appsSaved = [...new Set((result.analysisSummary?.apps || []).map((item) => item.app).concat((result.files || []).map((file) => file.app)))].filter(Boolean);
    // Every warning the run was saved with: the period ones and those confirmed with the server token.
    const acknowledged = review.apps.flatMap((item) => [...item.periodWarnings, ...item.warnings]);
    return (
      <section className="report-analysis-step" data-testid="step-result" data-state="success" aria-label={f.successTitle}>
        <NoticeBox variant={result.replayed ? "info" : acknowledged.length ? "warning" : "success"} title={result.replayed ? text.replayTitle : acknowledged.length ? text.resultWarningsTitle : f.successTitle} bordered>
          <div role="status" data-testid="result-success">
            <p>{result.replayed ? text.replayHint : f.successHint}</p>
            {result.cleanupPending && <p>{text.cleanupPending}</p>}
          </div>
        </NoticeBox>
        <Pairs items={[
          [text.store, review.context.storeName || "—"],
          [text.reportingPeriod, periodLabel(review.context.period, lang)],
          [text.mode, text.modes[review.context.mode] || "—"],
          [f.appsSaved, appsSaved.map((app) => appLabel(app, lang)).join("، ") || "—"],
        ]} />
        <div className="report-analysis-figures" data-testid="result-figures">
          <StatCard label={f.filesSaved} value={count((result.files || []).length, lang)} />
          <StatCard label={f.inserted} value={count(rows.insert ?? 0, lang)} />
          <StatCard label={f.updated} value={count(rows.update ?? 0, lang)} />
          <StatCard label={f.unchanged} value={count(rows.unchanged ?? 0, lang)} />
          {accounting && <StatCard label={text.preview2.rowsIncluded} value={count(accounting.includedRows, lang)} />}
          {accounting && <StatCard label={text.preview2.rowsExcluded} value={count(accounting.excludedRows, lang)} />}
          <StatCard label={f.warningsAcknowledged} value={count(acknowledged.length, lang)} />
          <StatCard label={f.staleRetained} value={count(staleCount(rows), lang)} />
        </div>
        {staleCount(rows) > 0 && <p className="report-analysis-muted">{f.staleExplain}</p>}
        {acknowledged.length > 0 && (
          <details className="report-preview-more">
            <summary>{f.warningsSummary(count(acknowledged.length, lang))}</summary>
            <IssueList items={acknowledged} kind="warning" text={text} lang={lang} context={context} acknowledged />
          </details>
        )}
        <div className="report-analysis-actions">
          <Button variant="primary" appearance="filled" onClick={onStartNew}>{f.startNew}</Button>
        </div>
      </section>
    );
  }

  if (error) {
    const code = error.code;
    const action = text.review.actions[code] || text.review.actions.fallback;
    const failing = review.apps.filter((item) => item.errors.length);
    const files = [...new Set(failing.flatMap((item) => item.errors.map((entry) => entry.fileName)).filter(Boolean))];
    const saved = error.details?.saved;
    const canRetry = RETRYABLE.has(code) || error.status === 0;
    return (
      <section className="report-analysis-step" data-testid="step-result" data-state="failure" aria-label={f.failureTitle}>
        <NoticeBox variant="danger" title={f.failureTitle} bordered>
          <div role="alert" data-testid="result-failure">
            <Pairs items={[
              [f.failureStage, text.review.phases[error.phase] || error.phase || "—"],
              [f.reason, `${errorText(text, error)}${code ? ` (${code})` : ""}`],
              ...(failing.length ? [[f.application, failing.map((item) => `${appLabel(item.app, lang)}${item.sections.length ? ` — ${item.sections.map((section) => sectionLabel(item.app, section, lang)).join("، ")}` : ""}`).join(" · ")]] : []),
              ...(files.length ? [[f.affectedFiles, files.join("، ")]] : []),
              [f.suggestedAction, typeof action === "function" ? action(failing[0] ? appLabel(failing[0].app, lang) : "") : action],
            ]} />
            <p data-testid="result-saved-state"><strong>{saved === false ? f.savedFalse : f.savedUnknown}</strong></p>
            <p className="report-analysis-muted">{f.filesKept}</p>
          </div>
        </NoticeBox>
        {failing.length > 0 && failing.map((item) => <IssueList key={item.app} items={item.errors} kind="error" text={text} lang={lang} context={context} />)}
        <div className="report-analysis-actions">
          <Button appearance="soft" onClick={onBackToFiles}>{f.backToFiles}</Button>
          {state.preview && <Button appearance="soft" onClick={onBackToPreview}>{f.backToPreview}</Button>}
          {canRetry && state.preview && <Button variant="primary" appearance="filled" onClick={onRetry}>{f.retrySave}</Button>}
        </div>
      </section>
    );
  }

  return null;
}
